import {createAsyncThunk} from '@reduxjs/toolkit';
import startOfToday from 'date-fns/startOfToday';
import {AppThunkApiConfig} from '../../createStore';
import {loadCalendarDays} from '../../data/calendar/actions';
import {loadBuildings} from '../../data/buildings/actions';
import {loadOpeningDaysExceptions} from '../../data/openingDays/actions';
import {loadConnections, loadWorkspaceReservationsForConnections} from '../../data/connections/actions';
import {
  loadLastSelectedWorkspaceReservationLocation,
  loadMyWorkspaceReservations,
} from '../../data/workspaceReservation/actions';
import {loadAllMeetings, loadMeetingPreferences} from '../../data/meetings/actions';
import {loadEnabledFeatures} from '../../infrastructure/features/actions';
import {loadSettings} from '../../data/settings/actions';
import {loadUser} from '../../data/user/actions';
import {getCalendarRange} from '../../data/calendar/utils';
import {loadMyParkingReservations} from '../../data/parkingReservation/actions';
import {loadCheckInStatus, loadConnectionsCheckInStatus} from '../../data/checkIn/actions';
import {hardRefreshWorkdays, loadWorkdays} from '../../data/workday';
import {loadExtras} from '../../data/extras';
import {FeatureToggle, loadNotificationPreferences, loadPreferences, timeslotsSlice} from '../../data';
import {actions} from '../../data/timeslots/actions';
import {addDays, addWeeks, endOfDay, endOfISOWeek, format, isAfter, startOfISOWeek} from 'date-fns';
import {getGlobalApiVersion, setGlobalApiVersion} from '@lib/infrastructure';
import {localStartOfDateForDateKey} from '@lib/logic';

export const loadAppData = createAsyncThunk<void, string | undefined, AppThunkApiConfig>(
  'ui/calendar/loadAppData',
  async (todayStr, {dispatch}) => {
    // Loading features is a must here because we cannot guarantee the order anymore,
    // when the calendar appeared the features are still loading (if not done here).
    const features = await dispatch(loadEnabledFeatures()).unwrap();

    const today = todayStr || format(new Date(), 'yyyy-MM-dd');
    const {days} = await dispatch(loadCalendarDays(today)).unwrap();
    const calendarRange = {...getCalendarRange(days), today};

    const [_settings, _preferences] = await Promise.all([
      dispatch(loadSettings()).unwrap(),
      dispatch(loadMeetingPreferences()).unwrap(),
      dispatch(loadPreferences()).unwrap(),
      dispatch(loadExtras()),
    ]);

    const calendarStart = startOfISOWeek(localStartOfDateForDateKey(today));
    const calendarEnd = endOfISOWeek(addWeeks(endOfDay(calendarStart), 4));

    // We're only allowed to request 30 days max, so for now let's hard code that instead
    // of trying to be smart and risking going over
    const fullRange = addDays(calendarStart, 30);
    if (isAfter(fullRange, calendarEnd)) {
      console.warn("Warning: calendar shows dates for which we haven't fetched reservations", {
        fetchedUntil: fullRange,
        calendarEnd,
      });
    }

    const timeslotsRange = {
      from: format(calendarStart, "yyyy-MM-dd'T'HH:mm"),
      to: format(fullRange, "yyyy-MM-dd'T'HH:mm"),
    };

    const timeslotsEnabled = features.includes(FeatureToggle.TimeslotsApi);

    if (!timeslotsEnabled) {
      setGlobalApiVersion('v1');
    }
    console.debug('Using API version:', getGlobalApiVersion());

    const reservationRelatedData = timeslotsEnabled
      ? [
          dispatch(actions.fetchWorkdays(timeslotsRange)),
          dispatch(actions.fetchWorkspaceReservations(timeslotsRange)),
          dispatch(actions.fetchWorkdaysForConnections(timeslotsRange)),
        ]
      : [
          dispatch(loadWorkspaceReservationsForConnections(calendarRange)).unwrap(),
          dispatch(loadMyWorkspaceReservations(calendarRange)).unwrap(),
          dispatch(loadWorkdays(calendarRange)).unwrap(),
        ];

    await Promise.all([
      dispatch(loadBuildings()).unwrap(),
      dispatch(loadConnections()),
      dispatch(loadOpeningDaysExceptions(calendarRange)).unwrap(),

      ...reservationRelatedData,

      // This is just a wrapper for reading from sync local storage in web
      // vs async local storage in native
      dispatch(loadLastSelectedWorkspaceReservationLocation()),

      dispatch(loadMyParkingReservations(calendarRange)).unwrap(),

      // No unwrap result with the check-in ones, they're silent
      dispatch(loadNotificationPreferences()),
      dispatch(loadCheckInStatus()),
      dispatch(loadConnectionsCheckInStatus()),
    ]);
  },
);

export const refreshAppData = createAsyncThunk<void, string | undefined, AppThunkApiConfig>(
  'ui/calendar/refreshAppData',
  async (todayStr, {dispatch}) => {
    // Loading features is a must here because we cannot guarantee the order anymore,
    // when the calendar appeared the features are still loading (if not done here).
    const features = await dispatch(loadEnabledFeatures()).unwrap();

    const today = todayStr || startOfToday().toISOString().slice(0, 'yyyy-MM-dd'.length);
    const {days} = await dispatch(loadCalendarDays(today)).unwrap();
    const calendarRange = {...getCalendarRange(days), today};

    const timeslotsEnabled = features.includes(FeatureToggle.TimeslotsApi);

    const [_settings, preferences] = await Promise.all([
      dispatch(loadSettings()).unwrap(),
      dispatch(loadMeetingPreferences()).unwrap(),
      dispatch(loadPreferences()).unwrap(),
      dispatch(loadExtras()),
    ]);

    dispatch(
      loadAllMeetings({
        ...calendarRange,
        pageSize: preferences.settings.pageTimespanInDays,
      }),
    );

    // TODO: ensure this is in range with calendar...
    const calendarStart = startOfISOWeek(new Date(today));
    const calendarEnd = endOfISOWeek(addWeeks(endOfDay(calendarStart), 4));

    // We're only allowed to request 30 days max, so for now let's hard code that instead
    // of trying to be smart and risking going over
    const fullRange = addDays(calendarStart, 30);
    if (isAfter(fullRange, calendarEnd)) {
      console.warn("Warning: calendar shows dates for which we haven't fetched reservations", {
        fetchedUntil: fullRange,
        calendarEnd,
      });
    }

    const timeslotsRange = {
      from: format(calendarStart, "yyyy-MM-dd'T'HH:mm"),
      to: format(fullRange, "yyyy-MM-dd'T'HH:mm"),
    };

    const reservationRelatedData = timeslotsEnabled
      ? [
          dispatch(timeslotsSlice.actions.clearAllChildReservations()),
          dispatch(actions.fetchWorkdays(timeslotsRange)),
          dispatch(actions.fetchWorkspaceReservations(timeslotsRange)),
          dispatch(actions.fetchWorkdaysForConnections(timeslotsRange)),
        ]
      : [
          dispatch(loadWorkspaceReservationsForConnections(calendarRange)).unwrap(),
          dispatch(loadMyWorkspaceReservations(calendarRange)).unwrap(),
          dispatch(hardRefreshWorkdays(calendarRange)).unwrap(),
        ];

    await Promise.all([
      dispatch(loadUser()).unwrap(),
      dispatch(loadBuildings()).unwrap(),
      dispatch(loadOpeningDaysExceptions(calendarRange)).unwrap(),
      dispatch(loadConnections()).unwrap(),

      ...reservationRelatedData,

      dispatch(loadMyParkingReservations(calendarRange)).unwrap(),

      //No unwrap result with the check-in ones, they're silent
      dispatch(loadCheckInStatus()),
      dispatch(loadConnectionsCheckInStatus()),
    ]);
  },
);
